import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { artistListState } from "../atom/atom";
import { addNewSong } from "../api/write.api";
import { extractYoutubeInfo } from "../api/common.api";
import { Genre } from "../types/type";
import { generateSongId } from "../api/fetch.api";
import FileUpload from "../components/file/FileUpload";
import { normalizeAudioFile } from "../tools/audio.tool";
import { useThumbnail } from "../hooks/useThumbnail";

interface INewSong {
  title: string;
  youtubeLink: string;
  youtubeId: string;
  thumbnailUrl: string;
  tagList: string[];
  isAI: boolean;
  makerUserId: string;
  tag: {
    genreList: string[];
    [key: string]: any;
  };
}

function AddSongPage() {
  const totalArtistList = useRecoilValue(artistListState);

  const [artistId, setArtistId] = useState<string>("");
  const [artistName, setArtistName] = useState<string>("");
  const [newSongList, setNewSongList] = useState<INewSong[]>([
    {
      title: "",
      youtubeLink: "",
      youtubeId: "",
      thumbnailUrl: "",
      tagList: [],
      isAI: true,
      makerUserId: "Homebrew",
      tag: { genreList: [] },
    },
  ]);
  const [tagList, setTagList] = useState<string[]>([]);
  const [genreList, setGenreList] = useState<string[]>([]);
  const [nextSongId, setNextSongId] = useState<string>("");
  const artistList = useRecoilValue(artistListState);

  const [audioProgressMessage, setAudioProgressMessage] = useState<string>("");
  const [thumbnailProgressMessage, setThumbnailProgressMessage] =
    useState<string>("");
  const [audioDownloadUrl, setAudioDownloadUrl] = useState<string>("");
  const [thumbnailDownloadUrl, setThumbnailDownloadUrl] = useState<string>("");
  const [resetKey, setResetKey] = useState<number>(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalArtistName, setModalArtistName] = useState<string | null>(null);

  const { totalArtistThumbnailList, fetchAllArtistThumbnailList } =
    useThumbnail();

  useEffect(() => {
    if (totalArtistList.length > 0) {
      fetchAllArtistThumbnailList();
      calculateNextSongId();
    }
  }, [totalArtistList.length]);

  const calculateNextSongId = async () => {
    const newId = await generateSongId();
    setNextSongId(newId);
  };

  const resetForm = () => {
    setArtistId("");
    setArtistName("");
    setNewSongList([
      {
        title: "",
        youtubeLink: "",
        youtubeId: "",
        thumbnailUrl: "",
        tagList: [],
        isAI: true,
        makerUserId: "Homebrew",
        tag: { genreList: [] },
      },
    ]);
    setTagList([]);
    setGenreList([]);
    setAudioProgressMessage("");
    setThumbnailProgressMessage("");
    setAudioDownloadUrl("");
    setThumbnailDownloadUrl("");
    calculateNextSongId(); // 다음 곡 ID를 다시 계산
    setResetKey((prevKey) => prevKey + 1); // FileUpload 컴포넌트 리셋
  };

  const handleArtistSelect = (artistId: string, artistName: string) => {
    setArtistId(artistId);
    setArtistName(artistName);
    setNewSongList((prevSongList) =>
      prevSongList.map((song) => ({
        ...song,
        tagList: [artistName],
      }))
    );
  };

  const handleYoutubeLinkChange = (index: number, link: string) => {
    const { youtubeId, thumbnailUrl } = extractYoutubeInfo(link);
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) =>
        i === index
          ? { ...song, youtubeLink: link, youtubeId, thumbnailUrl }
          : song
      )
    );
  };

  const handleToggleGenre = (index: number, genre: string) => {
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) => {
        if (i === index) {
          const genreIndex = song.tag.genreList.indexOf(genre);
          if (genreIndex === -1) {
            return {
              ...song,
              tag: {
                ...song.tag,
                genreList: [...song.tag.genreList, genre],
              },
            };
          } else {
            return {
              ...song,
              tag: {
                ...song.tag,
                genreList: song.tag.genreList.filter((g) => g !== genre),
              },
            };
          }
        }
        return song;
      })
    );
    setGenreList((prevList) =>
      prevList.includes(genre)
        ? prevList.filter((g) => g !== genre)
        : [...prevList, genre]
    );
  };

  const handleInputChange = (
    index: number,
    field: keyof INewSong,
    value: string | boolean
  ) => {
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) =>
        i === index ? { ...song, [field]: value } : song
      )
    );
  };

  const handleThumbnailSelect = async (thumbnailUrl: string, index: number) => {
    const newSong = newSongList.find((song, i) => i === index);
    if (newSong) {
      newSong.thumbnailUrl = thumbnailUrl;
      setIsModalOpen(false);
    }
  };

  const handleAddTag = (index: number, tag: string) => {
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) =>
        i === index
          ? {
              ...song,
              tag: { ...song.tag, tagList: [...song.tag.genreList, tag] },
            }
          : song
      )
    );
    setTagList((prevList) =>
      prevList.includes(tag)
        ? prevList.filter((t) => t !== tag)
        : [...prevList, tag]
    );
  };

  const handleAddRow = () => {
    setNewSongList((prevSongs) => [
      ...prevSongs,
      {
        title: "",
        youtubeLink: "",
        youtubeId: "",
        thumbnailUrl: "",
        tagList: artistName ? [artistName] : [],
        isAI: false,
        makerUserId: "Homebrew",
        tag: { genreList: [], tagList: [] },
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setNewSongList((prevSongs) => prevSongs.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const test = async () => {
      const newSongId = await generateSongId();
      console.log(newSongId);
    };
    test();
  }, []);

  const handleAddSong = async () => {
    try {
      await Promise.all(
        newSongList.map((song) =>
          addNewSong({
            artistId,
            artistName,
            url: audioDownloadUrl,
            ...song,
          })
        )
      );
      resetForm();
    } catch (error) {
      console.error("Error adding songs: ", error);
      alert("Failed to add songs.");
    }
  };

  const handleThumbnailClick = () => {
    if (!artistId) return window.alert("아티스트 선택 먼저");
    setModalArtistName(artistName); // 모달에 띄울 아티스트 설정
    setIsModalOpen(true); // 모달 열기
  };

  useEffect(() => {
    calculateNextSongId();
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <h1>Add New Songs</h1>
        <p>{nextSongId}</p>
      </div>
      <div>
        <label>Select Artist:</label>
        {artistList.map((artist) => (
          <button
            key={artist.artistId}
            onClick={() =>
              handleArtistSelect(artist.artistId, artist.artistName)
            }
            style={{
              backgroundColor:
                artist.artistId === artistId ? "lightblue" : "white",
              borderColor: artist.artistId === artistId ? "blue" : "grey",
              borderWidth: 0.1,
              marginRight: 4,
            }}
          >
            {artist.artistName}
          </button>
        ))}
      </div>
      {newSongList.map((song, index) => (
        <div
          key={index}
          style={{ marginTop: "20px", borderBottom: "1px solid #ccc" }}
        >
          <div>
            <label>Title:</label>
            <input
              type="text"
              value={song.title}
              onChange={(e) =>
                handleInputChange(index, "title", e.target.value)
              }
            />
          </div>
          {/* <div>
            <label>YouTube Link:</label>
            <input
              type="text"
              value={song.youtubeLink}
              onChange={(e) => handleYoutubeLinkChange(index, e.target.value)}
            />
          </div> */}
          <div>
            <label>Thumbnail URL:</label>
            <input type="text" value={song.thumbnailUrl} readOnly />

            <div style={{ display: "flex" }}>
              <FileUpload
                key={`thumbnail-${resetKey}-${index}`}
                storagePath={`/artistThumbnail/${artistName}/`}
                label="썸네일 업로드"
                accept="image/*"
                autoUpload={true}
                onStart={() =>
                  setThumbnailProgressMessage("썸네일 전처리중...")
                }
                onProgress={() =>
                  setThumbnailProgressMessage("썸네일 업로드 중...")
                }
                onSuccess={(downloadUrl: string) => {
                  setThumbnailProgressMessage("썸네일 업로드 완료");
                  setThumbnailDownloadUrl(downloadUrl);
                  setNewSongList((prevSongs) =>
                    prevSongs.map((song, i) =>
                      i === index
                        ? { ...song, thumbnailUrl: downloadUrl }
                        : song
                    )
                  );
                }}
                onError={(error) =>
                  setThumbnailProgressMessage(
                    `썸네일 업로드 실패 : ${error.message}`
                  )
                }
                onClickButton={() => {
                  if (!artistId) return window.alert("아티스트 선택 먼저");
                }}
              />
              <div>{thumbnailProgressMessage}</div>
              <button onClick={() => handleThumbnailClick()}>
                썸네일 선택
              </button>
            </div>
            <div>
              <label>Tags (Press Enter to add):</label>
              <input
                type="text"
                defaultValue={"homebrewRecommend"}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && e.currentTarget.value) {
                    handleAddTag(index, e.currentTarget.value);
                    e.currentTarget.value = "";
                  }
                }}
              />
              <div>
                {song.tagList.map((tag, tagIndex) => (
                  <span key={tagIndex} style={{ marginRight: "5px" }}>
                    {tag}
                  </span>
                ))}
              </div>
              <div>
                <label>Genre</label>
                {Object.values(Genre).map((genre) => (
                  <button
                    key={genre}
                    onClick={() => handleToggleGenre(index, genre)}
                    style={{
                      backgroundColor: song.tag.genreList.includes(genre)
                        ? "lightgreen"
                        : "white",
                      borderColor: song.tag.genreList.includes(genre)
                        ? "green"
                        : "grey",
                      borderWidth: 0.1,
                      marginRight: 4,
                    }}
                  >
                    {genre}
                  </button>
                ))}
                <div>
                  {song.tag.genreList.map((genre, genreIndex) => (
                    <span key={genreIndex} style={{ marginRight: "5px" }}>
                      {genre}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label>Is AI:</label>
              <input
                type="checkbox"
                checked={song.isAI}
                onChange={() => handleInputChange(index, "isAI", !song.isAI)}
              />
            </div>
            <div>
              <label>Maker User ID:</label>
              <input
                type="text"
                value={song.makerUserId}
                onChange={(e) =>
                  handleInputChange(index, "makerUserId", e.target.value)
                }
              />
            </div>
            {/* <button type="button" onClick={() => handleRemoveRow(index)}>
            Remove
          </button> */}
            <div style={{ display: "flex" }}>
              <FileUpload
                key={`audio-${resetKey}-${index}`}
                storagePath={`/`}
                fileName={`${nextSongId}.mp3`}
                label="오디오 파일 선택"
                accept="audio/*"
                autoUpload={true}
                onStart={() => setAudioProgressMessage("오디오 전처리중...")}
                onProgress={() =>
                  setAudioProgressMessage("오디오 업로드 중...")
                }
                onSuccess={(downloadUrl: string) => {
                  setAudioProgressMessage("오디오 업로드 완료");
                  setAudioDownloadUrl(downloadUrl);
                }}
                onError={(error) =>
                  setAudioProgressMessage(
                    `오디오 업로드 실패 : ${error.message}`
                  )
                }
                transformFile={normalizeAudioFile}
              />
              <div>{audioProgressMessage}</div>
            </div>
          </div>
        </div>
      ))}
      {/* <button type="button" onClick={handleAddRow}>
        row 추가
      </button> */}
      <button type="button" onClick={handleAddSong}>
        데이터 전송
      </button>
      {isModalOpen && modalArtistName && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>썸네일 선택 ({modalArtistName})</h2>
            <div className="thumbnail-selection-modal">
              {totalArtistThumbnailList[modalArtistName]?.map(
                (thumbnailUrl) => (
                  <img
                    key={thumbnailUrl}
                    src={thumbnailUrl}
                    alt="썸네일"
                    style={{ objectFit: "cover", width: 192, height: 100 }}
                    onClick={() => handleThumbnailSelect(thumbnailUrl, 0)}
                  />
                )
              )}
            </div>
            <button onClick={() => setIsModalOpen(false)}>닫기</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddSongPage;
